import { Component, forwardRef, inject, OnInit } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxCatalogService, ProductLabel } from 'hx-services';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { labelText } from '../catalog/catalog-text';

interface LabelOption {
  code: string;
  text: string;
}

/**
 * The tags of a card chosen from the dictionary «Метки товара» instead of typed free (so «Хит» and «хит» do not become two labels). A new label is made on the tab «Метки»,
 * not here. A tag of the card that is not in the dictionary (an old test tag, a disabled label) stays in the field marked, and can be taken off.
 * It writes nothing to the list of tags of the card class (the old `hx-tag-select` did on every change).
 */
@Component({
  selector: 'hc-label-select',
  templateUrl: './label-select.component.html',
  styleUrls: ['./label-select.component.less'],
  providers: [
    provideTranslocoScope('composite-products'),
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CatalogLabelSelectComponent), multi: true},
  ],
  imports: [FormsModule, RouterLink, TranslocoPipe, NzSelectComponent, NzOptionComponent]
})
export class CatalogLabelSelectComponent implements OnInit, ControlValueAccessor {
  protected options: LabelOption[] = [];
  protected colors: Record<string, string> = {};
  /** the label is not offered (disabled or unknown) but the card still carries it, so it stays visible and can be taken off: the key of the note next to it */
  protected notes: Record<string, string> = {};
  protected value: string[] = [];
  protected isDisabled = false;
  private known: ProductLabel[] = [];
  private readonly catalog = inject(HxCatalogService);
  private readonly tr = inject(TranslocoService);
  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  async ngOnInit(): Promise<void> {
    try {
      this.known = (await this.catalog.getProductLabels()).labels;
    } catch {
      // without the dictionary the field still shows (and lets off) the tags the card has
      this.known = [];
    }
    this.colors = Object.fromEntries(this.known.filter(l => !!l.color).map(l => [l.code, l.color as string]));
    this.rebuild();
  }

  writeValue(value: string[] | null): void {
    this.value = value ?? [];
    this.rebuild();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected select(value: string[]): void {
    this.value = value;
    this.rebuild();
    this.onChange(value);
    this.onTouched();
  }

  private rebuild(): void {
    const lang = this.tr.getActiveLang();
    const options: LabelOption[] = this.known.filter(l => l.enabled).map(l => ({code: l.code, text: labelText(lang, l.title) || l.code}));
    const notes: Record<string, string> = {};
    for (const code of this.value) {
      if (!options.some(o => o.code === code)) {
        const label = this.known.find(l => l.code === code);
        options.push({code, text: label ? labelText(lang, label.title) || code : code});
        notes[code] = label ? 'compositeProducts.labels.select.disabled' : 'compositeProducts.labels.select.unknown';
      }
    }
    this.options = options;
    this.notes = notes;
  }
}
